class SidalihFreeGiftController {
  constructor() {
    this.root = document.querySelector('[data-sidalih-free-gift-controller]');
    if (!this.root) return;

    this.popup = this.root.querySelector('#sidalih-free-gift-popup');

    this.shippingEnabled = this.root.dataset.enableShipping !== 'false';
    this.giftEnabled = this.root.dataset.enableGift !== 'false';

    this.shippingGoal = Number(this.root.dataset.freeShipping || 0) * 100;
    this.giftGoal = Number(this.root.dataset.giftThreshold || 0) * 100;

    this.messages = {
      shipping: this.root.dataset.shippingMessage || 'Spend [amount] more to get Free Shipping.',
      gift: this.root.dataset.giftMessage || 'Spend [amount] more to unlock your free gift.',
      success: this.root.dataset.successMessage || "Congratulations! You've unlocked your rewards."
    };

    this.currencySymbol =
      this.root.querySelector('.sidalih-riyal-symbol-template')?.innerHTML.trim() || '';

    this.cart = null;
    this.cartTotal = 0;
    this.hasGift = false;
    this.currentGiftLine = null;
    this.isLoadingGift = false;
    this.isRefreshing = false;
    this.refreshQueued = false;

    this.init();
  }

  async init() {
    this.applyFeatureState();
    this.bindPopupEvents();
    this.bindCartEvents();
    this.observeDynamicContent();
    this.interceptCartRequests();
    await this.refresh();
  }

  applyFeatureState() {
    document.documentElement.classList.toggle(
      'sidalih-free-shipping-disabled',
      !this.shippingEnabled
    );
    document.documentElement.classList.toggle(
      'sidalih-free-gift-disabled',
      !this.giftEnabled
    );
    document.documentElement.classList.toggle(
      'sidalih-free-gift-system-disabled',
      !this.shippingEnabled && !this.giftEnabled
    );
  }

  bindPopupEvents() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closePopup();
    });

    if (!this.popup) return;

    this.popup
      .querySelector('.sidalih-free-gift-popup__overlay')
      ?.addEventListener('click', () => this.closePopup());

    this.popup
      .querySelector('.sidalih-free-gift-popup__close')
      ?.addEventListener('click', () => this.closePopup());

    this.popup.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-gift-variant]');
      if (!button || !this.giftEnabled) return;

      event.preventDefault();
      button.disabled = true;
      button.classList.add('is-loading');

      try {
        await this.selectGift(button.dataset.giftVariant);
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    });
  }

  bindCartEvents() {
    ['cart:update', 'cart:reload', 'cart:refresh'].forEach((eventName) => {
      document.addEventListener(eventName, () => this.queueRefresh());
    });

    window.addEventListener('pageshow', () => this.queueRefresh());

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.queueRefresh();
    });
  }

  observeDynamicContent() {
    const observer = new MutationObserver((mutations) => {
      const hasRelevantUpdate = mutations.some((mutation) =>
        [...mutation.addedNodes].some((node) =>
          node.nodeType === 1 &&
          (
            node.matches?.(
              '[data-sidalih-free-gift-bar], hdt-quantity-wrapp, .hdt-mini-cart__actions'
            ) ||
            node.querySelector?.(
              '[data-sidalih-free-gift-bar], hdt-quantity-wrapp, .hdt-mini-cart__actions'
            )
          )
        )
      );

      if (!hasRelevantUpdate || !this.cart) return;

      this.updateAllBars();
      requestAnimationFrame(() => this.lockGiftQuantityControls());
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  interceptCartRequests() {
    if (window.__sidalihCartFetchIntercepted) return;
    window.__sidalihCartFetchIntercepted = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      const input = args[0];
      const url = typeof input === 'string' ? input : input?.url || '';

      if (/\/cart\/(add|change|update|clear)\.js(?:\?|$)/.test(url)) {
        setTimeout(() => this.queueRefresh(), 50);
      }

      return response;
    };
  }

  queueRefresh() {
    if (this.refreshQueued) return;
    this.refreshQueued = true;

    setTimeout(async () => {
      this.refreshQueued = false;
      await this.refresh();
    }, 80);
  }

  async getCart() {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });

    if (!response.ok) throw new Error('Unable to load cart.');
    return response.json();
  }

  async refresh() {
    if (this.isRefreshing) return;
    this.isRefreshing = true;

    try {
      const cart = await this.getCart();
      this.cart = cart;
      this.cartTotal = cart.total_price;
      this.hasGift = false;
      this.currentGiftLine = null;

      cart.items.forEach((item, index) => {
        if (item.properties?._sidalih_free_gift) {
          this.hasGift = true;
          this.currentGiftLine = index + 1;
        }
      });

      if (!this.giftEnabled && this.hasGift) {
        await this.removeGift();
        return;
      }

      if (this.giftEnabled && this.cartTotal < this.giftGoal && this.hasGift) {
        await this.removeGift();
        return;
      }

      this.updateAllBars();
      this.lockGiftQuantityControls();
      this.checkGiftEligibility();
    } catch (error) {
      console.error('Sidalih Free Gift:', error);
    } finally {
      this.isRefreshing = false;
    }
  }

  unlockGiftQuantityControls() {
    document
      .querySelectorAll('[data-sidalih-gift-locked="true"]')
      .forEach((wrapper) => {
        wrapper.classList.remove('sidalih-gift-quantity-locked');
        wrapper.removeAttribute('data-sidalih-gift-locked');

        const input = wrapper.querySelector('input[name="updates[]"]');

        if (input) {
          input.readOnly = false;
          input.removeAttribute('readonly');
          input.removeAttribute('aria-readonly');
          input.removeAttribute('max');
          input.removeAttribute('data-max');
          input.tabIndex = 0;
        }

        wrapper
          .querySelectorAll('button[name="minus"], button[name="plus"]')
          .forEach((button) => {
            button.disabled = false;
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
            button.tabIndex = 0;
          });
      });
  }

  lockGiftQuantityControls() {
    /*
      Kalles may reuse quantity-control elements after refreshing the
      cart drawer. Clear all previous locks first so a normal product
      never inherits the disabled state from the gift line.
    */
    this.unlockGiftQuantityControls();

    if (!this.giftEnabled || !this.hasGift || !this.currentGiftLine) return;

    const lineIndex = String(this.currentGiftLine);
    const quantityInputs = document.querySelectorAll(
      `input[name="updates[]"][data-index="${lineIndex}"]`
    );

    quantityInputs.forEach((input) => {
      const wrapper = input.closest(
        'hdt-quantity-wrapp, [data-quantity-wrapper]'
      );

      if (!wrapper) return;

      wrapper.classList.add('sidalih-gift-quantity-locked');
      wrapper.setAttribute('data-sidalih-gift-locked', 'true');

      input.value = '1';
      input.setAttribute('value', '1');
      input.setAttribute('min', '1');
      input.setAttribute('max', '1');
      input.setAttribute('data-max', '1');
      input.readOnly = true;
      input.setAttribute('aria-readonly', 'true');
      input.tabIndex = -1;

      wrapper
        .querySelectorAll('button[name="minus"], button[name="plus"]')
        .forEach((button) => {
          button.disabled = true;
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
          button.tabIndex = -1;
        });
    });
  }

  getPageDirection() {
    const htmlDirection = document.documentElement.getAttribute('dir');
    if (htmlDirection === 'rtl' || htmlDirection === 'ltr') return htmlDirection;

    return getComputedStyle(document.documentElement).direction === 'rtl'
      ? 'rtl'
      : 'ltr';
  }

  getActiveGoal() {
    if (this.shippingEnabled && this.giftEnabled) {
      return Math.max(this.shippingGoal, this.giftGoal, 1);
    }

    if (this.shippingEnabled) return Math.max(this.shippingGoal, 1);
    if (this.giftEnabled) return Math.max(this.giftGoal, 1);

    return 1;
  }

  updateAllBars() {
    const bars = document.querySelectorAll('[data-sidalih-free-gift-bar]');
    const maxGoal = this.getActiveGoal();
    const percent = Math.min((this.cartTotal / maxGoal) * 100, 100);
    const shippingPosition = Math.min((this.shippingGoal / maxGoal) * 100, 100);
    const giftPosition = Math.min((this.giftGoal / maxGoal) * 100, 100);
    const pageDirection = this.getPageDirection();

    bars.forEach((bar) => {
      bar.hidden = !this.shippingEnabled && !this.giftEnabled;
      if (bar.hidden) return;

      const progress = bar.querySelector('.sidalih-progress__bar');
      const message = bar.querySelector('.sidalih-progress-message');
      const shippingMilestone = bar.querySelector('.sidalih-milestone.shipping');
      const giftMilestone = bar.querySelector('.sidalih-milestone.gift');
      const track = bar.querySelector('.sidalih-progress-track');

      const shippingUnlocked =
        this.shippingEnabled && this.cartTotal >= this.shippingGoal;
      const giftUnlocked =
        this.giftEnabled && this.cartTotal >= this.giftGoal;

      bar.setAttribute('dir', pageDirection);
      bar.classList.toggle('is-shipping-disabled', !this.shippingEnabled);
      bar.classList.toggle('is-gift-disabled', !this.giftEnabled);

      if (progress) progress.style.width = `${percent}%`;

      if (shippingMilestone) {
        shippingMilestone.hidden = !this.shippingEnabled;
        shippingMilestone.style.setProperty(
          '--sidalih-milestone-position',
          `${shippingPosition}%`
        );
        shippingMilestone.classList.toggle('is-success', shippingUnlocked);
      }

      if (giftMilestone) {
        giftMilestone.hidden = !this.giftEnabled;
        giftMilestone.style.setProperty(
          '--sidalih-milestone-position',
          `${giftPosition}%`
        );
        giftMilestone.classList.toggle('is-success', giftUnlocked);
      }

      if (track) track.setAttribute('aria-valuenow', String(Math.round(percent)));
      if (message) message.innerHTML = this.getProgressMessage();

      bar.classList.toggle(
        'is-shipping-unlocked',
        shippingUnlocked && !giftUnlocked
      );
      bar.classList.toggle('is-gift-unlocked', giftUnlocked);
    });
  }

  getProgressMessage() {
    if (!this.shippingEnabled && !this.giftEnabled) return '';

    if (
      this.shippingEnabled &&
      this.cartTotal < this.shippingGoal &&
      (!this.giftEnabled || this.shippingGoal <= this.giftGoal)
    ) {
      return this.messages.shipping.replace(
        '[amount]',
        this.formatMoney(this.shippingGoal - this.cartTotal)
      );
    }

    if (this.giftEnabled && this.cartTotal < this.giftGoal) {
      return this.messages.gift.replace(
        '[amount]',
        this.formatMoney(this.giftGoal - this.cartTotal)
      );
    }

    return this.messages.success;
  }

  formatMoney(cents) {
    const amount = new Intl.NumberFormat(
      document.documentElement.lang || 'en',
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }
    ).format(cents / 100);

    return `${amount} ${this.currencySymbol}`;
  }

  checkGiftEligibility() {
    if (!this.popup || !this.giftEnabled) {
      this.closePopup();
      return;
    }

    const eligible = this.cartTotal >= this.giftGoal;

    if (eligible && !this.hasGift) {
      this.openPopup();
      return;
    }

    this.closePopup();
  }

  openPopup() {
    if (!this.popup || this.popup.classList.contains('is-open')) return;

    document.body.classList.add('sidalih-popup-open');
    this.popup.hidden = false;
    this.popup.classList.add('is-open');
  }

  closePopup() {
    if (!this.popup) return;

    document.body.classList.remove('sidalih-popup-open');
    this.popup.classList.remove('is-open');
    this.popup.hidden = true;
  }

  async addToCart(variantId, properties = {}) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, properties })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add gift.');
    }

    return response.json();
  }

  async changeItem(line, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to update cart.');
    }

    return response.json();
  }

  async selectGift(variantId) {
    if (!this.giftEnabled || this.hasGift || this.isLoadingGift) {
      this.closePopup();
      return;
    }

    this.isLoadingGift = true;

    try {
      await this.addToCart(variantId, { _sidalih_free_gift: 'true' });
      this.closePopup();
      document.dispatchEvent(new CustomEvent('cart:reload'));
      await this.refresh();
    } catch (error) {
      console.error('Sidalih Free Gift:', error);
    } finally {
      this.isLoadingGift = false;
    }
  }

  async removeGift() {
    if (!this.currentGiftLine) return;

    try {
      await this.changeItem(this.currentGiftLine, 0);
      this.hasGift = false;
      this.currentGiftLine = null;
      this.closePopup();
      document.dispatchEvent(new CustomEvent('cart:reload'));
      await this.refresh();
    } catch (error) {
      console.error('Failed to remove free gift:', error);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new SidalihFreeGiftController();
});
